import { SelectedPaymentOptions } from '@reward-platform/order-schemas/form-data'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDefaultPaymentOptions } from '~/hooks/useDefaultPaymentOptions/useDefaultPaymentOptions'
import { PlatformError } from '~/utils/errors'
import { BasketItemType } from '@reward-platform/order-schemas/basket'
import usePaymentOptionsStorage from '../hooks/storage/usePaymentOptions'

type PaymentOptionsContextValue = {
  paymentOptions: SelectedPaymentOptions | undefined
  setBalanceBoosterForItem: (itemType: BasketItemType, balanceBoosterCode: string | null) => void
  removeBalanceBoosterForItem: (itemType: BasketItemType) => void
  setPaymentOptions: (paymentOptions: SelectedPaymentOptions) => void
}

export const PaymentOptionsContext = createContext<PaymentOptionsContextValue | undefined>(
  undefined
)

type PaymentOptionsProviderProps = PropsWithChildren<{
  useStorage?: boolean
  onChangePaymentOptions?: (paymentOptions: SelectedPaymentOptions) => void
}>

type UsePaymentOptionsParams = {
  useStorage: boolean
}

type UsePaymentOptionsReturn = {
  paymentOptions: SelectedPaymentOptions | undefined
  setPaymentOptions: (paymentOptions: SelectedPaymentOptions) => void
}

const usePaymentOptions = ({ useStorage }: UsePaymentOptionsParams): UsePaymentOptionsReturn => {
  const defaultPaymentOptions = useDefaultPaymentOptions()
  const { storage, setStorage } = usePaymentOptionsStorage(true)
  const [paymentOptions, setPaymentOptions] = useState<SelectedPaymentOptions>()

  const isEnabledRef = useRef(true)
  useEffect(() => {
    if (isEnabledRef.current && defaultPaymentOptions) {
      isEnabledRef.current = false
      if (useStorage) {
        setStorage(defaultPaymentOptions)
      } else {
        setPaymentOptions(defaultPaymentOptions)
      }
    }
  }, [defaultPaymentOptions, setStorage, useStorage])

  if (useStorage) {
    return {
      paymentOptions: storage,
      setPaymentOptions: setStorage,
    }
  }

  return {
    paymentOptions,
    setPaymentOptions,
  }
}

export const usePaymentOptionsContext = () => {
  const context = useContext(PaymentOptionsContext)

  if (!context) {
    throw new PlatformError('usePaymentOptionsContext must be used within PaymentOptionsProvider')
  }

  return context
}

export const PaymentOptionsProvider = ({
  children,
  onChangePaymentOptions,
  useStorage,
}: PaymentOptionsProviderProps) => {
  const { paymentOptions, setPaymentOptions: setStoredPaymentOptions } = usePaymentOptions({
    useStorage: Boolean(useStorage),
  })

  const setPaymentOptions = useCallback(
    (updatedPaymentOptions: SelectedPaymentOptions) => {
      setStoredPaymentOptions(updatedPaymentOptions)
      onChangePaymentOptions?.(updatedPaymentOptions)
    },
    [onChangePaymentOptions, setStoredPaymentOptions]
  )

  const setBalanceBoosterForItem = useCallback(
    (itemType: BasketItemType, balanceBoosterCode: string | null) => {
      setPaymentOptions({
        ...paymentOptions,
        [itemType]: {
          ...paymentOptions?.[itemType],
          balanceBooster: balanceBoosterCode,
        },
      })
    },
    [paymentOptions, setPaymentOptions]
  )

  const removeBalanceBoosterForItem = useCallback(
    (itemType: BasketItemType) => setBalanceBoosterForItem(itemType, null),
    [setBalanceBoosterForItem]
  )

  const contextValue = useMemo<PaymentOptionsContextValue>(
    () => ({
      paymentOptions,
      setPaymentOptions,
      removeBalanceBoosterForItem,
      setBalanceBoosterForItem,
    }),
    [paymentOptions, removeBalanceBoosterForItem, setBalanceBoosterForItem, setPaymentOptions]
  )

  return (
    <PaymentOptionsContext.Provider value={contextValue}>{children}</PaymentOptionsContext.Provider>
  )
}
